import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faSpinner,
  faFloppyDisk,
} from "@fortawesome/free-solid-svg-icons";
import { Film } from "lucide-react";
import QuestionForm from "./QuestionForm";
import ExportPresentation from "./ExportPresentation";

export default function CreateRightPanel({
  activeTab,
  setActiveTab,
  activeQuestionTab,
  setActiveQuestionTab,
  handleAddSlide,
  activeSlide,
  slides,
  setSlides,
  animatedSlides,
  setAnimatedSlides,
  isCurrentSlideAnimated,
  setIsAnimateMode,
  slidesAnimationData,
  setSlidesAnimationData,
  handleCreateExport,
  createPresentationLoading,
  presentationId,
  getLinks,
}) {
  const currentSlide =
    activeSlide !== null ? slides[activeSlide] : null;

  return (
    <div
      className="col-span-3 bg-white rounded-2xl border-l border-gray-200 p-4 overflow-y-auto h-[38rem] flex flex-col justify-between"
      style={{ scrollbarWidth: "thin" }}
    >
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          Component Library
        </h2>

        <div className="flex space-x-2 text-sm text-gray-600 mb-4">
          <button
            className={`px-3 py-1 rounded-lg ${
              activeTab === "interactive"
                ? "bg-lightPurple/10 text-lightPurple"
                : "hover:bg-gray-100"
            }`}
            onClick={() => setActiveTab("interactive")}
          >
            Interactive Questions
          </button>

          <button
            className={`px-3 py-1 rounded-lg ${
              activeTab === "content"
                ? "bg-lightPurple/10 text-lightPurple"
                : "hover:bg-gray-100"
            }`}
            onClick={() => setActiveTab("content")}
          >
            Content
          </button>
        </div>

        {/* Interactive Tab */}
        {activeTab === "interactive" && (
          <div className="space-y-6 pl-5">
            <div className="flex space-x-2 mb-4 text-sm">
              {["poll", "multiple", "text"].map((type) => (
                <button
                  key={type}
                  onClick={() => setActiveQuestionTab(type)}
                  className={`px-3 py-1 rounded-lg capitalize transition ${
                    activeQuestionTab === type
                      ? "bg-lightPurple text-white"
                      : "bg-gray-100 hover:bg-lightPurple/20 text-gray-700"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>

            {activeQuestionTab === "poll" && (
              <QuestionForm
                type="poll"
                label="Poll Question"
                placeholder="Enter Poll question"
                onSave={handleAddSlide}
              />
            )}

            {activeQuestionTab === "multiple" && (
              <QuestionForm
                type="multiple"
                label="Multiple Question"
                placeholder="Enter Multiple question"
                onSave={handleAddSlide}
              />
            )}

            {activeQuestionTab === "text" && (
              <QuestionForm
                type="text"
                label="Text Question"
                placeholder="Enter Text question"
                onSave={handleAddSlide}
              />
            )}
          </div>
        )}

        {/* Content Tab */}
        {activeTab === "content" && currentSlide && (
          <div className="space-y-6 pl-5">
            <div className="space-y-6">
              {/* Active Toggle */}
              <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-800">
                  Slide Settings
                </h2>

                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs font-bold uppercase ${
                      currentSlide.is_active
                        ? "text-green-600"
                        : "text-gray-400"
                    }`}
                  >
                    {currentSlide.is_active ? "Active" : "Inactive"}
                  </span>

                  <button
                    onClick={() => {
                      const updatedSlides = [...slides];
                      updatedSlides[activeSlide].is_active =
                        !updatedSlides[activeSlide].is_active;
                      setSlides(updatedSlides);
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                      currentSlide.is_active
                        ? "bg-lightPurple"
                        : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white ${
                        currentSlide.is_active
                          ? "translate-x-6"
                          : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Animation Toggle */}
              <div className="flex items-start justify-between py-3 border-b border-gray-100">
                <div>
                  <p className="text-sm font-semibold text-gray-700">
                    Animated Slide
                  </p>

                  <p className="text-xs text-gray-400 mt-0.5">
                    {currentSlide.type === "image"
                      ? "Use slide image as canvas background"
                      : "Add animations to this slide"}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs font-bold uppercase ${
                      isCurrentSlideAnimated
                        ? "text-purpleColor"
                        : "text-gray-400"
                    }`}
                  >
                    {isCurrentSlideAnimated ? "ON" : "OFF"}
                  </span>

                  <button
                    onClick={() =>
                      setAnimatedSlides((prev) => ({
                        ...prev,
                        [activeSlide]: !prev[activeSlide],
                      }))
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                      isCurrentSlideAnimated
                        ? "bg-purpleColor"
                        : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white ${
                        isCurrentSlideAnimated
                          ? "translate-x-6"
                          : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              </div>

              {isCurrentSlideAnimated && (
                <button
                  onClick={() => setIsAnimateMode(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-purpleColor hover:bg-[#7a1b51] text-white text-sm font-semibold rounded-xl"
                >
                  <Film className="w-4 h-4" />
                  Open Animation Editor
                </button>
              )}

              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider">
                Slide Content : {activeSlide + 1}
              </h3>

              {/* Image */}
              {currentSlide.type === "image" && (
                <div className="flex items-center justify-center h-40 border rounded-xl bg-gray-50 text-gray-500 text-base font-medium">
                  No Question Available For Editing
                </div>
              )}

              {/* Blank */}
              {currentSlide.type === "blank" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background Color
                  </label>

                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={
                        slidesAnimationData[activeSlide]
                          ?.canvasBackgroundColor || "#1a1a2e"
                      }
                      onChange={(e) => {
                        const color = e.target.value;

                        setSlidesAnimationData((prev) => ({
                          ...prev,
                          [activeSlide]: {
                            ...(prev[activeSlide] || {}),
                            canvasBackgroundColor: color,
                          },
                        }));
                      }}
                      className="w-10 h-10"
                    />

                    <input
                      type="text"
                      value={
                        slidesAnimationData[activeSlide]
                          ?.canvasBackgroundColor || "#1a1a2e"
                      }
                      onChange={(e) => {
                        const color = e.target.value;

                        setSlidesAnimationData((prev) => ({
                          ...prev,
                          [activeSlide]: {
                            ...(prev[activeSlide] || {}),
                            canvasBackgroundColor: color,
                          },
                        }));
                      }}
                      className="border rounded-lg p-2 text-sm w-32"
                    />
                  </div>
                </div>
              )}

              {/* Question */}
              {currentSlide.type !== "image" &&
                currentSlide.type !== "blank" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Question
                      </label>

                      <input
                        type="text"
                        value={currentSlide.question || ""}
                        onChange={(e) => {
                          const updatedSlides = [...slides];
                          updatedSlides[activeSlide].question =
                            e.target.value;
                          setSlides(updatedSlides);
                        }}
                        className="w-full border rounded-lg p-2 text-sm"
                      />
                    </div>

                    {(currentSlide.question_type === "poll" ||
                      currentSlide.question_type === "multiple") && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Choices
                        </label>

                        {currentSlide.choices?.map((choice, i) => (
                          <div
                            key={i}
                            className="flex items-center space-x-2 mb-2"
                          >
                            <input
                              type="text"
                              value={choice.choice_text || choice}
                              onChange={(e) => {
                                const updatedSlides = [...slides];
                                updatedSlides[activeSlide].choices[i] =
                                  e.target.value;
                                setSlides(updatedSlides);
                              }}
                              className="w-full border rounded-lg p-2 text-sm"
                            />

                            <button
                              onClick={() => {
                                const updatedSlides = [...slides];
                                updatedSlides[activeSlide].choices =
                                  updatedSlides[
                                    activeSlide
                                  ].choices.filter(
                                    (_, idx) => idx !== i
                                  );
                                setSlides(updatedSlides);
                              }}
                              className="px-2 py-1 text-xs bg-red-100 text-red-600 rounded-lg"
                            >
                              Remove
                            </button>
                          </div>
                        ))}

                        <button
                          onClick={() => {
                            const updatedSlides = [...slides];
                            updatedSlides[activeSlide].choices = [
                              ...(updatedSlides[activeSlide].choices || []),
                              "",
                            ];
                            setSlides(updatedSlides);
                          }}
                          className="mt-2 px-3 py-2 bg-lightPurple text-white rounded-lg text-sm"
                        >
                          <FontAwesomeIcon
                            icon={faPlus}
                            className="mr-1"
                          />
                          Add Choice
                        </button>
                      </div>
                    )}
                  </>
                )}
            </div>
          </div>
        )}
      </div>

      <div>
        <div className="flex justify-end my-5 border-t border-slate-300 pt-4">
          <button
            onClick={handleCreateExport}
            disabled={createPresentationLoading}
            className={`px-4 py-2 bg-lightPurple text-white rounded-md ${
              createPresentationLoading
                ? "cursor-not-allowed bg-lightPurple/60"
                : "hover:bg-purpleColor"
            }`}
          >
            {createPresentationLoading ? (
              <>
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="animate-spin mr-2"
                />
                Creating...
              </>
            ) : (
              <>
                <FontAwesomeIcon
                  icon={faFloppyDisk}
                  className="mr-2"
                />
                Save Presentation
              </>
            )}
          </button>
        </div>

        <ExportPresentation
          presentationId={presentationId}
          slides={slides}
          getLinks={getLinks}
        />
      </div>
    </div>
  );
}